import {
  SidePanel,
  Button,
  PanelSection,
  Autocomplete,
  DatePicker,
  Callout,
  FormField,
} from '@dev-spendesk/grapes';
import { isBefore } from 'date-fns';
import { type FormikErrors, useFormik } from 'formik';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import {
  type BudgetStatus,
  type FilterSettings,
} from 'src/core/modules/budgets/models/budgetFilters';

type BudgetStatusOption = { key: BudgetStatus; label: string };

type Props = {
  filterSetting: FilterSettings;
  onChangeFilterSetting: (filterSetting: FilterSettings) => void;
  onClose: () => void;
};

export const FilterPanel = ({
  filterSetting,
  onChangeFilterSetting,
  onClose,
}: Props) => {
  const { t } = useTranslation('global');
  const statusOptions: BudgetStatusOption[] = [
    { key: 'active', label: t('budget.budgetaryExercise.active') },
    { key: 'future', label: t('budget.budgetaryExercise.future') },
    { key: 'closed', label: t('budget.budgetaryExercise.closed') },
  ];

  const formikProps = useFormik<FilterSettings>({
    initialValues: filterSetting,
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: (values) => {
      onChangeFilterSetting(values);
      onClose();
    },
    validate: (values) => {
      const errors: FormikErrors<FilterSettings> = {};
      if (
        values.period.end &&
        values.period.start &&
        isBefore(values.period.end, values.period.start)
      ) {
        errors.period = {
          end: t('budget.redesigned.filters.errors.endBeforeStart'),
        };
      }
      return errors;
    },
  });

  return (
    <SidePanel
      title={t('budget.redesigned.filters.selectFilters')}
      onClose={onClose}
      footer={
        <div className="flex gap-16">
          <Button
            variant="secondaryNeutral"
            fit="parent"
            text={t('misc.reset')}
            onClick={() => {
              formikProps.setValues({
                status: undefined,
                period: { start: undefined, end: undefined },
              });
              formikProps.handleSubmit();
            }}
          />
          <Button
            variant="primaryBrand"
            fit="parent"
            text={t('misc.apply')}
            onClick={() => {
              formikProps.handleSubmit();
            }}
          />
        </div>
      }
    >
      <PanelSection>
        <FormField
          label={t('budget.redesigned.filters.status')}
          className="mb-24"
        >
          <Autocomplete<BudgetStatusOption>
            fit="parent"
            options={statusOptions}
            value={statusOptions.find(
              (option) => option.key === formikProps.values.status,
            )}
            placeholder={t('budget.redesigned.filters.selectStatus')}
            onSearch={() => {
              // Intentionally left blank. Search is not used in this filter.
            }}
            onSelect={(option) => {
              formikProps.setFieldValue('status', option?.key);
            }}
            showClearSelectionButton
          />
        </FormField>
        <FormField
          label={t('budget.redesigned.filters.period')}
          className="mb-8"
        >
          <div className="flex gap-16">
            <div className="w-full">
              <DatePicker
                value={formikProps.values.period.start}
                onChange={(date) => {
                  formikProps.setValues({
                    ...formikProps.values,
                    period: {
                      ...formikProps.values.period,
                      start: date,
                    },
                  });
                }}
                placeholder={t('misc.from')}
                fit="parent"
              />
            </div>
            <div className="w-full">
              <DatePicker
                value={formikProps.values.period.end}
                onChange={(date) => {
                  formikProps.setValues({
                    ...formikProps.values,
                    period: {
                      ...formikProps.values.period,
                      end: date,
                    },
                  });
                }}
                placeholder={t('misc.to')}
                fit="parent"
                placement="bottom-end"
              />
            </div>
          </div>
          {formikProps.errors.period?.end && (
            <Callout
              title={formikProps.errors.period.end}
              variant="alert"
              className="mt-8"
            />
          )}
        </FormField>
      </PanelSection>
    </SidePanel>
  );
};
